import { createAsyncThunk } from "@reduxjs/toolkit";

import * as checkoutAPI from "../../../api/checkout";
import { IAddress } from "../../../models/address";

/**
 * Return type for loadShippingAndBillingAddresses thunk.
 */
interface LoadAddressesResult {
    shipping: Partial<IAddress>;
    billing: Partial<IAddress>;
    billingIsShipping: boolean;
    shouldSetStepToShippingAddress: boolean;
}

/**
 * Load cached shipping and billing addresses.
 *
 * Retrieves the shipping and billing addresses from the previous checkout
 * session. Compares the addresses to determine if billing equals shipping.
 * If no cached shipping address is found, sets the checkout step to shipping
 * address entry.
 *
 * @returns {Promise<LoadAddressesResult>} Promise resolving to addresses and comparison result
 *
 * @example
 * // Load addresses on checkout page mount
 * dispatch(loadShippingAndBillingAddresses());
 *
 * @sideEffects
 * - Updates shipping address form fields via extraReducers
 * - Updates billing address form fields via extraReducers
 * - Updates `form.billing_addr_is_shipping_addr` via extraReducers
 * - May update `form.current_step` to SHIPPING_ADDRESS if no cached address
 * - Logs to console in debug mode
 */
export const loadShippingAndBillingAddresses = createAsyncThunk(
    "checkout/loadShippingAndBillingAddresses",
    async (): Promise<LoadAddressesResult> => {
        console.debug("Loading cached shipping and billing addresses...");

        const data = await Promise.all([
            checkoutAPI.loadShippingAddress(),
            checkoutAPI.loadBillingAddress(),
        ]);

        const compareFields: (keyof IAddress)[] = [
            "first_name",
            "last_name",
            "line1",
            "line2",
            "line4",
            "postcode",
            "state",
            "country",
            "phone_number",
        ];

        const shipping = data[0] || {};
        const billing = data[1] || {};

        const billingIsShipping = compareFields.reduce((memo, name) => {
            if (
                shipping[name] &&
                billing[name] &&
                shipping[name] !== billing[name]
            ) {
                memo = false;
            }
            return memo;
        }, true);

        const hasShippingAddress = Object.keys(shipping).length > 0;
        const shouldSetStepToShippingAddress = !hasShippingAddress;

        if (shouldSetStepToShippingAddress) {
            console.debug(
                "No cached shipping addr returned from API, setting checkout step to SHIPPING_ADDRESS",
            );
        }

        return {
            shipping,
            billing,
            billingIsShipping,
            shouldSetStepToShippingAddress,
        };
    },
);
